import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import BusinessInsuranceView from '@/website/components/NewLandingPages/businessInsurance/index';
import TopPromotionCountDownView from '@/website/components/NewLandingPages/components/topPromotionCountDown/common';
import { getRequestLanguage } from '@/website/constants/locales';
import { UrlQueryParamtersStorageEnum, useUrlQueryParameters } from '@/website/hooks/useUrlQueryParameters';
import Layout from '@/website/layouts/newLandingPages';
import { ContentfulPageProps, getContentfulData } from '@/website/lib/getContentfulData';
import { useOrganizationLandingPageStore } from '@/website/store/OrganizationLandingPageStore';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { buildCanonicalUrl } from '@/website/util/canonical';
import { parseServerSideCookies } from '@/website/util/cookies';
import { getBusinessInsuranceWebsiteCrawlerHeadData } from '@/website/util/crawler';
import { getRouteLocalePrefix } from '@/website/util/localeUrl';
import { businessInsuranceKey, getBusinessInsuranceLandingPageThemeMap } from '@jove-frontend/uikit';

const SLUG = 'business-insurance';
export async function getServerSideProps(context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<ContentfulPageProps>> {
  const {
    req: { headers },
    query,
  } = context;
  const { country } = parseServerSideCookies(headers?.cookie);
  const language = getRequestLanguage(context.locale, headers?.cookie);
  const routeLocalePrefix = getRouteLocalePrefix(context.resolvedUrl);
  const pageLocale = context.locale?.toLowerCase();
  const isDefaultLocale = !pageLocale || pageLocale === 'default' || pageLocale === 'en';
  const isUk = isDefaultLocale && !routeLocalePrefix && ((query.country as string) || country || 'GB').toUpperCase() === 'GB';
  const crawlerData = getBusinessInsuranceWebsiteCrawlerHeadData(language);

  return getContentfulData(context, SLUG, {
    metaInfo: {
      title: crawlerData.title,
      description: isUk
        ? crawlerData.description
        : "Business insurance for EU contractors and freelancers. Professional indemnity, public liability and employers' liability cover across 30+ European countries. Instant quotes. FCA and ASF regulated.",
      images: '/business-insurance-landing.png',
    },
    useNewLogic: true,
    dataFileName: 'business-insurance-landing-data.json',
    activeLanguage: language,
  });
}

function BusinessInsurance(props: ContentfulPageProps) {
  const { post, promotion, metadata } = props;
  const router = useRouter();
  const { updateStoredQueryParams } = useUrlQueryParameters({
    urlKey: UrlQueryParamtersStorageEnum.landingPage,
  });
  const {
    actions: { initialize },
  } = useOrganizationLandingPageStore();
  const {
    state: { hidePromotionLandingPageHeader, hidePromotionLandingPageFooter },
    actions: { updatePromotionDetailInfo },
  } = promotionInfoStore();

  /** Promotion logic */
  const hasActivePromotion = promotion?.status === 'active';

  /**
   * effects
   */
  useEffect(() => {
    updateStoredQueryParams();
    if (router.query['promotionSlug']) {
      initialize(router.query['promotionSlug'] as string);
    }
  }, []);

  useEffect(() => {
    updatePromotionDetailInfo(promotion);
  }, [promotion]);

  return (
    <>
      <Head>
        <title>{metadata?.openGraph?.title}</title>
        <meta name="description" content={metadata?.openGraph?.description} />
        <link
          key="canonical"
          rel="canonical"
          href={buildCanonicalUrl(router.asPath, process.env['NX_WEBSITE_URL'], router.locale, router.query.country as string)}
        />
        <meta content={metadata?.openGraph?.title} property="og:title" key="og-title" />
        <meta content={metadata?.openGraph?.description} property="og:description" key="og-description" />
        <meta name="twitter:title" content={metadata?.openGraph?.title} />
        <meta name="twitter:description" content={metadata?.openGraph?.description} />
      </Head>
      <Layout.Wrapper>
        {hasActivePromotion && <TopPromotionCountDownView />}
        {!hidePromotionLandingPageHeader && (
          <Layout.Header
            customLogoUrl={promotion?.brandingLogo}
            promotion={promotion}
            insuranceType={businessInsuranceKey}
            productScheme={getBusinessInsuranceLandingPageThemeMap().sme.value}
          />
        )}
        <Layout.Content containterStyles={{ backgroundColor: promotion?.brandingData?.primary_colour || '#EBEFFE' }}>
          <BusinessInsuranceView post={post} promotion={promotion} />
        </Layout.Content>
        {!hidePromotionLandingPageFooter && <Layout.Footer />}
      </Layout.Wrapper>
    </>
  );
}

export default BusinessInsurance;
